import Link from 'next/link';
import { useState } from 'react';
import { IoArrowForward, IoCloseCircleOutline, IoCloudUploadOutline, IoLinkOutline } from 'react-icons/io5';

import { ActionIcon, Box, Button, Card, Group, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE, MIME_TYPES } from '@mantine/dropzone';
import { modals } from '@mantine/modals';

import { Collateral } from 'models';
import { CollateralHelpers } from 'helpers/CollateralHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { VideoHelpers } from 'helpers/VideoHelpers';

import { CollateralCover } from 'components/collateral/CollateralCover';
import { CollateralModal } from 'components/collateral-editor/CollateralModal';
import { SortableList } from 'components/company-profile-edit/components/SortableList';
import { EmptyMessage } from 'components/empty-message/EmptyMessage';
import { useAction } from 'hooks/use-action';
import { CollateralService } from 'services/CollateralService';
import { FileService } from 'services/FileService';
import { LocalNotificationService } from 'services/LocalNotificationService';

import cx from './CollateralEditor.module.scss';

type CollateralOwner = { company: string } | { product: string };

const CollateralEditor = ({
    owner,
    collateral,
    href,
    onChange,
    onReorder,
}: {
    owner: CollateralOwner;
    collateral: Collateral[];
    href: (item: Collateral) => string;
    onChange: () => Promise<unknown>;
    onReorder?: (ordered: Collateral[]) => Promise<unknown>;
}) => {
    const [editing, setEditing] = useState<Collateral | null>(null);
    const [renameQueue, setRenameQueue] = useState<Collateral[]>([]);
    const [url, setUrl] = useState('');

    const openedModal = editing ?? renameQueue[0] ?? null;

    const closeModal = () => {
        if (editing) {
            setEditing(null);
            return;
        }

        setRenameQueue((queue) => queue.slice(1));
    };

    const fileGroup =
        'company' in owner ? `company:${owner.company}:collateral` : `component:${owner.product}:collateral`;

    const createFromUploadedFile = (file: { id: string; name?: string | null; filename?: string | null }) =>
        CollateralService.create({
            ...owner,
            file: file.id,
            title: CollateralHelpers.titleFromFilename(file.name || file.filename || '') || 'Collateral',
        });

    const [upload, uploading] = useAction(async (files: FileWithPath[]) => {
        const results = await Promise.allSettled(
            files.map(async (file) => createFromUploadedFile(await FileService.create({ file, group: fileGroup }))),
        );
        const created = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
        const failures = results.filter((result) => result.status === 'rejected');

        await onChange();
        setRenameQueue((queue) => [...queue, ...created]);

        if (failures.length) {
            console.error(failures.map((failure) => failure.reason));
            LocalNotificationService.showError({
                message: `Could not upload ${IntlHelpers.formatCount(failures.length, 'file', 'files')}. Please try again.`,
            });
        }
    });

    const [addVideoLink, addingVideoLink] = useAction(async () => {
        const trimmedUrl = url.trim();

        if (!trimmedUrl) {
            return;
        }

        if (!VideoHelpers.parseLink(trimmedUrl)) {
            LocalNotificationService.showError({ message: 'Paste a YouTube or Vimeo link.' });
            return;
        }

        try {
            const created = await CollateralService.create({ ...owner, videoUrl: trimmedUrl, title: '' });
            await onChange();

            setUrl('');
            setRenameQueue((queue) => [...queue, created]);
        } catch (error) {
            console.error(error);
            LocalNotificationService.showError({ message: 'Could not add that video. Please try again.' });
        }
    });

    const openDeleteModal = (item: Collateral) => {
        modals.openConfirmModal({
            centered: true,
            title: `Delete ${item.title}?`,
            children: <Text size="sm">Visitors will no longer be able to open it.</Text>,
            labels: { confirm: 'Yes, delete', cancel: 'Keep it' },
            confirmProps: { variant: 'light', color: 'red' },
            onConfirm: async () => {
                await CollateralService.delete(item.id);
                await onChange();
            },
        });
    };

    const renderItem = (item: Collateral) => (
        <SortableList.Item
            title={item.title}
            placeholder={
                CollateralHelpers.isViewable(item) ? (
                    <CollateralCover collateral={item} width={144} alt={item.title} iconSize={28} />
                ) : undefined
            }
            description={CollateralHelpers.caption(item)}
            actions={
                <Button size="xs" variant="subtle" component={Link} href={href(item)}>
                    View
                </Button>
            }
            onEdit={() => setEditing(item)}
            onDelete={() => openDeleteModal(item)}
        />
    );

    const renderList = () => {
        if (collateral.length === 0) {
            return <EmptyMessage>No collateral yet. Upload a file or add a video link above.</EmptyMessage>;
        }

        if (onReorder) {
            return <SortableList items={collateral} onReorder={onReorder} renderItem={renderItem} />;
        }

        return (
            <Stack gap="sm">
                {collateral.map((item) => (
                    <Card key={item.id} withBorder p="sm">
                        <Group gap="md" wrap="nowrap" align="center">
                            {renderItem(item)}
                        </Group>
                    </Card>
                ))}
            </Stack>
        );
    };

    return (
        <>
            <Stack gap="lg">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <Dropzone
                        onDrop={upload}
                        loading={uploading}
                        accept={[MIME_TYPES.pdf, ...IMAGE_MIME_TYPE, MIME_TYPES.mp4, 'video/quicktime', 'video/webm']}
                        multiple
                        maxSize={50 * 1024 * 1024}
                        onReject={() =>
                            LocalNotificationService.showError({
                                message:
                                    'Files must be PDFs, images or videos under 50 MB. For longer videos, add a YouTube or Vimeo link.',
                            })
                        }
                        p={0}
                        className={cx.dropzone}
                        classNames={{ inner: cx.dropzoneInner }}
                    >
                        <Stack className={cx.option} style={{ pointerEvents: 'none' }}>
                            <Box className={cx.optionIcon}>
                                <Dropzone.Accept>
                                    <IoCloudUploadOutline color="var(--mantine-color-blue-6)" />
                                </Dropzone.Accept>
                                <Dropzone.Reject>
                                    <IoCloseCircleOutline color="var(--mantine-color-red-6)" />
                                </Dropzone.Reject>
                                <Dropzone.Idle>
                                    <IoCloudUploadOutline />
                                </Dropzone.Idle>
                            </Box>

                            <Box ta="center">
                                <Text fw={600}>Upload a file</Text>
                                <Text size="sm" c="dimmed">
                                    Drop a file or browse, max 50 MB
                                </Text>
                            </Box>

                            <Group gap={8}>
                                {['pdf', 'jpg', 'png', 'mp4'].map((extension) => (
                                    <Box key={extension} className={cx.fileType} data-extension={extension}>
                                        {extension}
                                    </Box>
                                ))}
                            </Group>
                        </Stack>
                    </Dropzone>

                    <Stack className={cx.option}>
                        <Box className={cx.optionIcon}>
                            <IoLinkOutline />
                        </Box>

                        <Box ta="center">
                            <Text fw={600}>Add a video</Text>
                            <Text size="sm" c="dimmed">
                                Paste a YouTube or Vimeo link
                            </Text>
                        </Box>

                        <TextInput
                            w="100%"
                            aria-label="YouTube or Vimeo link"
                            placeholder="https://youtu.be/..."
                            value={url}
                            onChange={(event) => setUrl(event.currentTarget.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();

                                    addVideoLink();
                                }
                            }}
                            rightSectionWidth={36}
                            rightSection={
                                <ActionIcon
                                    size={28}
                                    radius="sm"
                                    variant="filled"
                                    color="dark"
                                    onClick={addVideoLink}
                                    loading={addingVideoLink}
                                    disabled={!url.trim()}
                                    aria-label="Add video"
                                >
                                    <IoArrowForward />
                                </ActionIcon>
                            }
                        />
                    </Stack>
                </SimpleGrid>

                {renderList()}
            </Stack>

            {openedModal && (
                <CollateralModal
                    key={openedModal.id}
                    collateral={openedModal}
                    href={href(openedModal)}
                    justUploaded={!editing}
                    onSaved={onChange}
                    onClose={closeModal}
                />
            )}
        </>
    );
};

export { CollateralEditor };
